//crop-form.tsx

import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import React, { ChangeEvent, useContext } from "react";
import {
  Formik,
  Form,
  FastField,
  FieldProps,
  useFormikContext,
  FormikHelpers,
} from "formik";
import * as yup from "yup";
import {
  FormControl,
  FormHelperText,
  Box,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { AppTextField } from "./text-field";
import { validationMessages as vm } from "../utils/validation/messages";
import { unitSystemLabel, unitSystemLabelOther } from "../pages/crops-page";
import { UserContext } from "./app-routes";
import { FormPrompt } from "./form-prompt";
import { Seed } from "../models/seed";
import { UnitSystem } from "../models/grower";
import { Tray } from "../models/tray";
import { Grower } from "../models/grower";

const cropValidationScheme = yup.object({
  name: yup.string().trim().required(vm.Required),
  seedId: yup
    .number()
    .typeError("Select a seed")
    .required(vm.Required)
    .min(1, vm.Required),
  trayId: yup.number().nullable(),
  seedingRate: yup
    .number()
    .typeError(vm.Number)
    .positive(vm.Positive)
    .required(vm.Required),
  seedingRateOther: yup.number().typeError(vm.Number).positive(vm.Positive),
  overnightSoak: yup.boolean().required(vm.Required),
  soakTimeMinutes: yup
    .number()
    .typeError(vm.Number)
    .integer(vm.Integer)
    .min(0, vm.Min)
    .required(vm.Required),
  germinationDays: yup
    .number()
    .typeError(vm.Number)
    .integer(vm.Integer)
    .min(0, vm.Min)
    .required(vm.Required),
  blackoutDays: yup
    .number()
    .typeError(vm.Number)
    .integer(vm.Integer)
    .min(0, vm.Min)
    .required(vm.Required),
  daysToMaturity: yup
    .number()
    .typeError(vm.Number)
    .integer(vm.Integer)
    .positive(vm.Positive)
    .required(vm.Required),
  cutYield: yup.number().typeError(vm.Number).positive(vm.Positive),
  notes: yup.string().max(5000),
  archived: yup.boolean().required(vm.Required),
  bufferPercentage: yup
    .number()
    .typeError(vm.Number)
    .min(0, vm.Min)
    .required(vm.Required),
  shortCode: yup
    .string()
    .trim()
    .min(2, "Minimum 2 characters")
    .max(4, "Maximum 4 characters")
    .matches(/^[A-Za-z0-9]*$/, "Only letters and numbers")
    .optional(),
});

export interface CropFormValues {
  id: number;
  name: string;
  shortCode: string;
  seedId: number | "";
  trayId: number | "";
  seedingRate: string;
  seedingRateOther: string;
  overnightSoak: boolean;
  soakTimeMinutes: string;
  germinationDays: string;
  blackoutDays: string;
  daysToMaturity: string;
  cutYield?: string;
  notes: string;
  archived: boolean;
  bufferPercentage: string;
}

export const gramsToOz = (grams: number): number => {
  return grams / 28.35;
};

export const ozToGrams = (oz: number): number => {
  return oz * 28.35;
};

export const convertToUnitSystem = (
  unitSystem: UnitSystem,
  value: number,
): number => {
  return unitSystem === "metric" ? ozToGrams(value) : gramsToOz(value);
};

export const otherUnitSystem = (unitSystem: UnitSystem): UnitSystem => {
  return unitSystem === "imperial" ? "metric" : "imperial";
};

const SeedingRateField = () => {
  const { values, touched, errors, setFieldValue, handleChange } =
    useFormikContext<CropFormValues>();

  const context = useContext(UserContext);
  const grower = context.grower;

  if (!grower) {
    throw new Error("Invalid context");
  }

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleChange(e);

    let seedingRateOther: string;

    const seedingRate = parseFloat(e.target.value);

    if (Number.isNaN(seedingRate)) {
      seedingRateOther = "";
    } else {
      seedingRateOther = convertToUnitSystem(
        otherUnitSystem(grower.UnitSystem),
        seedingRate,
      ).toFixed(2);
    }

    setFieldValue("seedingRateOther", seedingRateOther, false);
  };

  return (
    <Box mt={2}>
      <TextField
        id="seedingRate"
        name="seedingRate"
        label={`Seeding Rate (${unitSystemLabel(grower)})`}
        value={values.seedingRate}
        fullWidth
        variant="outlined"
        error={touched.seedingRate && Boolean(errors.seedingRate)}
        helperText={touched.seedingRate && errors.seedingRate}
        onChange={onChange}
      />
    </Box>
  );
};

const SeedingRateOtherField = () => {
  const { values, errors, touched, setFieldValue, handleChange } =
    useFormikContext<CropFormValues>();

  const context = useContext(UserContext);
  const grower = context.grower;

  if (!grower) {
    throw new Error("Invalid context");
  }

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleChange(e);

    let seeingRateFieldValue: string;

    const seedingRateOther = parseFloat(e.target.value);

    if (Number.isNaN(seedingRateOther)) {
      seeingRateFieldValue = "";
    } else {
      seeingRateFieldValue = convertToUnitSystem(
        grower.UnitSystem,
        seedingRateOther,
      ).toFixed(2);
    }

    setFieldValue("seedingRate", seeingRateFieldValue, false);
  };

  return (
    <Box mt={2}>
      <TextField
        id="seedingRateOther"
        name="seedingRateOther"
        label={`Seeding Rate (${unitSystemLabelOther(grower)})`}
        value={values.seedingRateOther}
        fullWidth
        variant="outlined"
        error={touched.seedingRateOther && Boolean(errors.seedingRateOther)}
        helperText={touched.seedingRateOther && errors.seedingRateOther}
        onChange={onChange}
      />
    </Box>
  );
};

type CropFormProps = React.PropsWithChildren<{
  initialValues: CropFormValues;
  saved: boolean;
  formId?: string;
  onSubmit: (
    values: CropFormValues,
    helpers: FormikHelpers<CropFormValues>,
  ) => void | Promise<void>;
  seeds: Seed[];
  trays: Tray[];
  grower: Grower;
}>;

export default function CropForm(props: CropFormProps) {
  const context = useContext(UserContext);
  const grower = context.grower;

  if (!grower) {
    throw new Error("Invalid context");
  }

  const seedOptionLabel = (seed?: Seed) =>
    seed ? `${seed.name} ${seed.variety ? " - " + seed.variety : ""}` : "";

  const formId = props.formId ?? "crop-form";

  return (
    <Formik
      initialValues={props.initialValues}
      validationSchema={cropValidationScheme}
      onSubmit={async (values, helpers) => {
        await props.onSubmit(values, helpers);
        helpers.resetForm({ values });
      }}
    >
      {(form) => (
        <>
          <FormPrompt form={form} saved={props.saved} />
          <Form id={formId} noValidate autoComplete="off">
            <Box>
              <FormControl
                variant="standard"
                error={form.touched.archived && Boolean(form.errors.archived)}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      name="archived"
                      color="primary"
                      onChange={form.handleChange}
                      checked={form.values.archived}
                    />
                  }
                  label="Archived"
                />
                {form.touched.archived && form.errors.archived && (
                  <FormHelperText>{form.errors.archived}</FormHelperText>
                )}
              </FormControl>
            </Box>
            <Box
              mt={1}
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
                border: "1px solid rgba(0,0,0,0.15)",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#ebebeb",
                  px: 2,
                  py: 1,
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  borderBottom: "1px solid rgba(0,0,0,0.1)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#666", fontWeight: 600 }}
                >
                  Crop ID
                </Typography>
              </Box>
              <Box sx={{ px: 2, py: 1 }}>
                <Box
                  display="flex"
                  gap={2}
                  mt={0.1}
                  mb={1}
                  sx={{
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  {" "}
                  <Box
                    flex={3.5}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    {" "}
                    <FastField name="name">
                      {(fieldProps: FieldProps) => (
                        <AppTextField
                          {...fieldProps}
                          autoFocus
                          required
                          label="Crop Name"
                        />
                      )}
                    </FastField>
                  </Box>
                  <Box
                    flex={1.5}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    {" "}
                    <FastField name="shortCode">
                      {(fieldProps: FieldProps) => (
                        <AppTextField
                          {...fieldProps}
                          label="Crop Code (RECOMMENDED)"
                          inputProps={{
                            maxLength: 4,
                            style: { textTransform: "uppercase" },
                          }}
                          helperText="Two- to four-character code for batch numbers"
                        />
                      )}
                    </FastField>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              mt={1}
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
                border: "1px solid rgba(0,0,0,0.15)",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#ebebeb",
                  px: 2,
                  py: 1,
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  borderBottom: "1px solid rgba(0,0,0,0.1)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#666", fontWeight: 600 }}
                >
                  Crop Seeding
                </Typography>
              </Box>
              <Box sx={{ px: 2, py: 0.5 }}>
                <Box
                  display="flex"
                  gap={2}
                  mb={0.1}
                  mt={1}
                  alignItems="flex-start"
                  sx={{
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box flex={2} sx={{ width: { xs: "100%", sm: "auto" } }}>
                    <SeedingRateField />
                  </Box>

                  <Box flex={2} sx={{ width: { xs: "100%", sm: "auto" } }}>
                    <SeedingRateOtherField />
                  </Box>

                  <Box flex={2} sx={{ width: { xs: "100%", sm: "auto" } }}>
                    <FastField
                      component={AppTextField}
                      name="soakTimeMinutes"
                      label="Soak Time (Minutes)"
                    />
                  </Box>
                </Box>

                <Box
                  display="flex"
                  gap={2}
                  mb={0.1}
                  mt={1}
                  alignItems="flex-start"
                  sx={{
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  {props.grower.Plan === "pro" && (
                    <Box flex={2} sx={{ width: { xs: "100%", sm: "auto" } }}>
                      <FastField name="trayId">
                        {(fieldProps: FieldProps) => (
                          <AppTextField {...fieldProps} select label="Tray">
                            <MenuItem value="">None</MenuItem>
                            {props.trays
                              .filter(
                                (t) =>
                                  t.archivedAt === null ||
                                  props.initialValues.trayId === t.id,
                              )
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((v) => (
                                <MenuItem key={v.id} value={v.id}>
                                  {v.name}
                                </MenuItem>
                              ))}
                          </AppTextField>
                        )}
                      </FastField>
                    </Box>
                  )}

                  <Box flex={2} sx={{ width: { xs: "100%", sm: "auto" } }}>
                    <FastField name="seedId">
                      {(fieldProps: FieldProps) => (
                        <AppTextField {...fieldProps} select label="Seed">
                          {props.seeds
                            .filter(
                              (s) =>
                                s.archivedAt === null ||
                                props.initialValues.seedId === s.id,
                            )
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((v) => (
                              <MenuItem key={v.id} value={v.id}>
                                {seedOptionLabel(v)}
                              </MenuItem>
                            ))}
                        </AppTextField>
                      )}
                    </FastField>
                  </Box>
                </Box>

                <Box mt={1}>
                  <FormControl
                    variant="standard"
                    error={
                      form.touched.overnightSoak &&
                      Boolean(form.errors.overnightSoak)
                    }
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="overnightSoak"
                          color="primary"
                          onChange={form.handleChange}
                          checked={form.values.overnightSoak}
                        />
                      }
                      label="Overnight Seed Soak"
                    />
                    {form.touched.overnightSoak &&
                      form.errors.overnightSoak && (
                        <FormHelperText>
                          {form.errors.overnightSoak}
                        </FormHelperText>
                      )}
                  </FormControl>
                </Box>
              </Box>
            </Box>

            <Box
              mt={1}
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
                border: "1px solid rgba(0,0,0,0.15)",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#ebebeb",
                  px: 2,
                  py: 1,
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  borderBottom: "1px solid rgba(0,0,0,0.1)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#666", fontWeight: 600 }}
                >
                  Crop Cycle
                </Typography>
              </Box>
              <Box sx={{ px: 2, py: 1 }}>
                <Box
                  display="flex"
                  gap={2}
                  mt={0.1}
                  mb={2}
                  sx={{
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box
                    flex={1}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <FastField
                      component={AppTextField}
                      name="germinationDays"
                      label="Days to Germinate"
                    />
                  </Box>

                  <Box
                    flex={1}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <FastField
                      component={AppTextField}
                      name="blackoutDays"
                      label="Days in Blackout"
                    />
                  </Box>

                  <Box
                    flex={1}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <FastField
                      component={AppTextField}
                      name="daysToMaturity"
                      label="Days to Maturity"
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              mt={2}
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
                border: "1px solid rgba(0,0,0,0.15)",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#ebebeb",
                  px: 2,
                  py: 1,
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  borderBottom: "1px solid rgba(0,0,0,0.1)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#666", fontWeight: 600 }}
                >
                  Crop Yield
                </Typography>
              </Box>

              <Box sx={{ px: 2, py: 1 }}>
                <Box
                  display="flex"
                  gap={2}
                  mt={0.1}
                  mb={2}
                  sx={{
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box
                    flex={1}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <FastField
                      component={AppTextField}
                      name="cutYield"
                      label={`Cut Yield (${unitSystemLabel(grower)})`}
                    />
                  </Box>

                  <Box
                    flex={1}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <FastField
                      component={AppTextField}
                      name="bufferPercentage"
                      label="Cut Yield Buffer (%)"
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              mt={2}
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
                border: "1px solid rgba(0,0,0,0.15)",
                mb: 5,
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#ebebeb",
                  px: 2,
                  py: 1,
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  borderBottom: "1px solid rgba(0,0,0,0.1)",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "#666", fontWeight: 600 }}
                >
                  Additional Crop Details
                </Typography>
              </Box>

              <Box sx={{ px: 2, pt: 1, pb: 3 }}>
                <FastField name="notes">
                  {(fieldProps: FieldProps) => (
                    <AppTextField
                      {...fieldProps}
                      multiline
                      label="Notes"
                      rows={4}
                    />
                  )}
                </FastField>
              </Box>
            </Box>
            {/* Spacer so last fields don't hide behind sticky action bar */}
            <Box sx={{ height: 110 }} />

            {/* Action buttons (or any footer) injected by parent */}
            {props.children}
          </Form>
        </>
      )}
    </Formik>
  );
}
